/** Finalized Admin Onboarding v2.1 copy (YAR-285). */
import { SMS_CONSENT_TEXT, SMS_CONSENT_VERSION } from '@/config/smsConsent';

export const ONBOARDING_COPY = {
  welcome: {
    badge: 'Beta',
    headline: 'The family organizer for busy parents',
    subtitle: 'Less to hold in your head. More room to be present.',
    googleCta: 'Continue with Google',
    waitlist: 'Don’t use Gmail? Join the waitlist.',
    consentPrefix: 'By continuing, you agree to our',
    terms: 'Terms of Service',
    and: 'and',
    privacy: 'Privacy Policy',
    personalGmailTitle: 'Use a personal Gmail',
    personalGmailBody:
      'Yaruta works with personal Gmail accounts only. Continue with a personal @gmail.com.',
  },
  founderLetter: {
    paragraphs: [
      'Hi,',
      'I’m George, founder of Yaruta and a father of two. After my Parkinson’s diagnosis, I felt how precious time with family really is.',
      'I built Yaruta to give families more time together, and less time lost to the details of everyday life. As you let us into your family’s world, we’ll work to protect that and earn your trust.',
      'Thank you for being here. This is just the beginning.',
    ],
    signatureName: 'George Tellechea',
    signatureTitle: 'Founder & CEO, Yaruta',
    cta: 'Continue',
  },
  /**
   * The create-vs-join fork that follows sign-in (YAR-438 / YAR-439). Copy
   * governed verbatim by the YAR-439 copy table, approved 2026-09-13. Two
   * identity-framed options; each check row is first person and describes
   * what that path commits the parent to.
   */
  fork: {
    title: 'How will you use Yaruta?',
    options: {
      admin: {
        label: 'I get the school emails',
        checks: [
          "I'll connect my Gmail",
          "I'll review what Yaruta finds",
          "I'll invite everyone else",
        ],
      },
      helper: {
        label: "I'm here to help",
        checks: [
          "I'll wait for the household admin to send me an invite",
          "I'll tap the link in the email to join",
        ],
      },
    },
    cta: 'Continue',
    /** Shown under the button when creating the household fails; the choice stays uncommitted. */
    error: 'Something went wrong. Please try again.',
  },
  /**
   * The join wait-state (YAR-439). Static; never creates a household. The
   * signed-in email is rendered inline after `checkInvitePrefix`, in
   * semibold, and wraps — it is never truncated.
   */
  waitState: {
    title: "You're here to help",
    blurb:
      "Nothing to do yet. Once your household admin sets up the family in Yaruta, they'll send you an invite by email.",
    checkInvitePrefix: 'Your invite will arrive at ',
    checkTap: 'Tap the link in the email to join',
    reassurance: 'Sign out anytime. The invite link will bring you back.',
    setUpInstead: 'Set up my family instead',
    signOut: 'Sign out',
  },
  /** Shared by the fork and the wait-state; unchanged from the copy table. */
  signOutModal: {
    title: 'Sign out of Yaruta?',
    body: 'You can sign back in with the same Google account anytime.',
    confirm: 'Sign out',
    cancel: 'Cancel',
  },
  /**
   * Orientation carousel. Each card is a short headline plus a supporting
   * line — the previous single sentence per card was folded into the two
   * (Figma `Demo-1 / default` 9349:14570 … `Demo-4 / default` 9349:14573).
   * The subtitles carry a full stop; the headlines do not.
   *
   * The Figma frames still draw four carousel dots. Those are stale — the
   * flow uses the global progress bar plus `CarouselDots`, which is what the
   * component already renders.
   */
  carousel: {
    cards: [
      {
        title: 'Choose who matters',
        subtitle: 'Emails from senders you approve.',
      },
      {
        title: 'You have the final say',
        subtitle: 'We pull tasks and events you confirm.',
      },
      {
        title: 'Share with your people',
        subtitle: 'You choose who sees your schedule.',
      },
      {
        title: 'Enjoy more time together',
        subtitle: 'Your whole week, in one calm view.',
      },
    ],
    next: 'Next',
    continue: 'Continue',
  },
  profile: {
    title: 'Set up your profile',
    avatarCaption: 'Tap to change image',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. John Smith',
    roleLabel: 'Role',
    rolePlaceholder: 'Select a role',
    timezoneLabel: 'Time zone',
    timezonePlaceholder: 'Select your current time zone',
    cta: 'Continue',
  },
  children: {
    title: 'Add profiles for your children',
    subtitle: 'So every task and event reaches the right child.',
    avatarCaption: 'Tap to change image',
    addRow: 'Add child',
    // Sentence case per the Dev Notes global rule and Kelvin's Jul 21 note
    // on YAR-158 ("make sure the titles are sentence-case").
    formTitleAdd: 'Add child',
    formTitleEdit: 'Edit child',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. John Smith',
    gradeLabel: 'Grade',
    gradePlaceholder: 'Select a grade', // Figma says "Select a role" — template bug (open question #2)
    schoolLabel: 'School',
    schoolPlaceholder: 'Search their school',
    schoolSearching: 'Searching schools...',
    schoolNoResults: 'No schools found. Type your school name.',
    schoolUseCustomName: 'Use custom name',
    formCtaAdd: 'Add child',
    formCtaEdit: 'Save changes',
    cta: 'Continue',
  },
  members: {
    title: 'Invite your circle',
    // The timing is stated because it changed (YAR-435): nothing is mailed
    // from this step any more, so promising an invite here without saying
    // when would be the same silent mismatch the ticket was filed about.
    subtitle:
      'Invite partners or grandparents. We’ll send each invite once you finish setting up.',
    inviteRow: 'Invite member',
    formTitleAdd: 'Add member',
    formTitleEdit: 'Edit member',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. John Smith',
    emailLabel: 'Email',
    emailPlaceholder: 'e.g. johnsmith@example.com',
    accessLabel: 'Access Level',
    accessPlaceholder: 'Select access level',
    // Access-level option labels/descriptions now come from the shared
    // ONBOARDING_INVITE_ACCESS_OPTIONS (Can edit / Can view) so onboarding and
    // Settings speak one vocabulary; the old accessFull/accessViewOnly copy was
    // dropped with the single-option ("Full access") dropdown.
    formCtaAdd: 'Add member',
    formCtaEdit: 'Save changes',
    saveInvites: 'Save invites',
    skip: 'I’ll do this later in settings',
  },
  gmail: {
    title: 'Connect Gmail, safely',
    // YAR-160 AC1, approved in Meeting #114 (Aug 11): aligned with the
    // privacy policy — the scan previews sender, subject and ~200 characters.
    subtitle: 'Yaruta previews recent emails, then reads senders you approve.',
    // Shown instead of `cta` once the signed-in account has been blocked.
    // Yaruta connects the mailbox you are signed in with (YAR-325), so a
    // blocked account has nothing to retry — the recovery is to come back as
    // an account that owns a usable mailbox.
    ctaBlocked: 'Sign in with a different account',
    // Order and content follow Figma `Email monitoring` (7555:19777).
    // "Verified by Google" leads — it is the reassurance a parent needs
    // before granting mailbox access, so it precedes the mechanics. It
    // replaces the former "Private and secure" bullet, whose promise
    // ("never sold or shared") is now carried by this one.
    bullets: [
      {
        title: 'Verified by Google',
        description:
          'Meets Google’s strict security standard. Your data is never sold or shared.',
        info: 'Google checked that Yaruta keeps your information safe. Nothing is stored until you confirm a task or event.',
      },
      {
        title: 'Only senders you approve',
        description: 'Only emails from senders you approve. Nothing else.',
      },
      {
        title: 'Read-only access',
        description:
          'Reads new emails to find tasks and events. Never sends, replies, deletes, or edits them.',
        info: 'Yaruta adds a Gmail label and filter for each approved sender. That’s how it finds their new emails as they arrive, using read-only access.',
      },
      {
        title: 'You stay in control',
        description:
          'Edit or remove any tasks and events Yaruta finds. Disconnect Gmail anytime.',
      },
    ],
    cta: 'Connect with Gmail',
    // YAR-160 AC6 error state, approved in Meeting #114 (Aug 11 2026). Shown
    // when the parent returns from Google having left one or more requested
    // permissions switched off. It tells them what to do on the RETRY, which
    // only reads as an instruction because the primary CTA still says
    // "Connect with Gmail" (YAR-372) — the previous wording asked them to
    // "reconnect and allow all requested permissions" while the only button
    // on screen signed them out.
    permissionsDeclined:
      'Some permissions were left off. Turn them all on to connect.',
    // The alert's DEFAULT state (YAR-160 AC6, Figma 7555:19777): always on
    // screen above the CTA, blue/info, so the parent knows before they reach
    // Google's consent screen that every box starts unchecked. Replaced —
    // never stacked — by `permissionsDeclined` on return with scopes missing.
    permissionsHint:
      'Google starts every permission off. Turn them all on to connect.',
    privacyPolicy: 'Privacy Policy',
    checking: 'Checking connection...',
    connected: 'You’re connected!',
    error: 'Couldn’t connect',
    errorBody: 'Check your internet, then try again.',
    tryAgain: 'Try again',
    // YAR-332: refusals from `gmail-forwarding-setup` that are not network
    // faults, keyed by the server's stable `code`. Each replaces the generic
    // error card above. Only GMAIL_PROFILE_UNAVAILABLE is retryable; the
    // other two cannot be fixed from this account, so they say who can and
    // offer a way back instead of "Try again".
    setupErrors: {
      NOT_HOUSEHOLD_PRIMARY: {
        message: 'Only your household admin can connect Gmail',
        description: 'Ask your household admin to connect their Gmail.',
        action: 'Go back',
      },
      NOT_HOUSEHOLD_MEMBER: {
        message: 'You’re not in this household',
        description: 'Ask your household admin for a new invite.',
        action: 'Go back',
      },
      GMAIL_PROFILE_UNAVAILABLE: {
        message: 'Gmail didn’t confirm your account',
        description: 'Reconnect Gmail to try again.',
        action: 'Reconnect Gmail',
      },
    },
  },
  digest: {
    headline: 'Get a daily text digest',
    subtitle:
      'One text each evening with tomorrow’s tasks and events. Turn it off anytime.',
    phoneLabel: 'Phone number',
    phonePlaceholder: '(555) 123-4567',
    phoneHint: 'Pick your country, then enter your number.',
    phoneError: 'Enter a valid phone number.',
    consentVersion: SMS_CONSENT_VERSION,
    consentText: SMS_CONSENT_TEXT,
    // The disclosure block itself (sentence, checkbox, Terms/Privacy links)
    // is rendered by the shared SmsConsentDisclosure component (YAR-459).
    cta: 'Continue',
    skip: 'I’ll do this later in settings',
    verifyTitle: 'Verify your number',
    verifyIntro: (phone: string) =>
      `We sent a 6-digit verification code to ${phone}`,
    verifyCodeLabel: 'Verification code',
    codePlaceholder: '000000',
    verifyCta: 'Verify',
    changeNumber: 'Wrong number? Change it',
    wrongCode: 'Wrong verification code. Try again.',
    resend: 'Resend code',
    // mm:ss. Derives the minutes digit instead of hardcoding `0:` so a cooldown
    // of 60s or more still renders (the current window is 30s, which this
    // formats byte-identically to the old one).
    resendCooldown: (seconds: number) =>
      `Resend code in ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`,
    timeTitle: 'When should we text you?',
    timeSubtitle:
      'We’ll send your daily digest in the evening. Change it anytime.',
    timeLabel: 'Send time',
    timeCta: 'Continue',
  },
  troubleshooting: {
    title: 'Troubleshooting',
    items: [
      // Post-CASA set, approved in Meeting #114 (Aug 11 2026) and recorded
      // verbatim on YAR-160 AC5 + its Troubleshooting sub-flow. Eight items
      // became five: the three pre-CASA entries went away because they
      // described a review that has since completed — "this app isn't
      // verified" and "Access blocked" were both symptoms of the pending
      // verification, and the work/school entry is now handled by the
      // personal-Gmail hard stop on the Hero before a parent ever reaches
      // this screen. The retired Google security notice is absorbed into
      // item 2, and the label/filter explanation moved to the "Read-only
      // access" bullet's tooltip on the Connect Gmail screen itself.
      // Do not reword without design sign-off — kelvin owns these strings.
      {
        q: '1. Google is asking me to choose what Yaruta can access',
        a: 'Google shows each permission turned off to start. Turn on all, or choose “Select all.” Yaruta needs all permissions to find your school and activity emails, file them under a label, and keep watching for new ones.',
      },
      {
        q: '2. Google is asking me to verify myself',
        a: 'That’s a standard Google security check. Follow the prompt on your phone or enter the code Google sent you, then come back here to finish connecting.',
      },
      {
        q: '3. The connection isn’t finishing',
        a: 'Connecting can take a few minutes. If it still hasn’t finished, check your internet and tap “Connect with Gmail” again. If that doesn’t work, close the Google tab, reopen Yaruta, and start again from this screen. Nothing you’ve set up is lost.',
      },
      {
        q: '4. Do I have to connect Gmail?',
        a: 'Yes. Connecting Gmail is how Yaruta finds your family’s tasks and events, so setup can’t finish without it. You approve which senders Yaruta reads, and you can disconnect anytime from Settings.',
      },
      {
        q: '5. What can Yaruta see in my inbox?',
        a: 'To suggest senders, Yaruta looks at your recent emails: the sender, the subject, and the first few lines. That’s how it finds which ones come from your schools. Yaruta doesn’t keep any of it. After you approve senders, Yaruta reads only new emails from those senders.',
      },
    ],
  },
  senders: {
    title: 'Choose your senders',
    subtitle:
      'Select the senders you recognize, or add any that are missing. Everything else stays untouched.',
    listHeader: 'Senders',
    chosen: (n: number) => `${n} chosen`,
    suggestedChip: 'Suggested',
    allSendersBadge: 'All senders',
    allSendersInfo: (domain: string) =>
      `Adds everyone who emails from ${domain}`,
    scanning: 'Finding recent senders...',
    emptyTitle: 'No senders found yet',
    emptyBody: (email: string) =>
      `Nothing to suggest from ${email} yet. You can add senders manually.`,
    helper: 'Choose at least one sender to continue.',
    addMore: 'Add more senders',
    addEmpty: 'Add senders',
    cta: 'Continue',
  },
  addSenders: {
    title: 'Add more senders',
    fieldLabel: 'Email address or domain',
    placeholder: 'Search by name, email, or domain...',
    add: 'Add',
    commit: (n: number) => (n === 1 ? 'Add 1 sender' : `Add ${n} senders`),
    invalidError:
      'Enter a valid email (teacher@school.org) or domain (@school.org).',
    duplicateError: 'This sender has already been added.',
    searchError: "Couldn't search your inbox. Type the full address to add it.",
    // Separate from `searchError` because the cause is different and so is
    // what the parent should conclude: the mailbox was never queried, and
    // retrying the field cannot help. Says so plainly rather than implying
    // the inbox was searched and came back empty.
    searchUnavailable:
      'Your Gmail session expired, so we can’t search your inbox. Type the full address to add it.',
    // Secondary line on a dropdown row the mailbox search matched on message
    // CONTENT rather than on the sender's own name or address (YAR-374) — a
    // school mailing through a platform whose From line names the teacher or
    // the platform, not the school (YAR-361). Without it the row reads as a
    // broken filter. Wording pending PM review.
    mentionedIn: (term: string) => `Mentioned "${term}" in an email`,
  },
  summary: {
    headline: '🎉 Welcome to Yaruta!',
    subtitle:
      'Here’s what’s ready. Tasks and events from your approved senders show up on your schedule, and nothing’s added until you confirm it.',
    cardTitle: 'Setup Summary',
    childrenTitle: (n: number) =>
      n === 1 ? '1 Child added' : `${n} Children added`,
    membersTitle: (n: number) =>
      n === 1 ? '1 Member invited' : `${n} Members invited`,
    membersSkippedTitle: 'Members invited',
    membersSkipped: 'Skipped',
    emailMonitoringTitle: 'Email monitoring',
    emailMonitoringDetail: 'Active & verified',
    sendersTitle: 'Approved senders',
    sendersDetail: (n: number) => `${n} approved`,
    digestTitle: 'Daily digest',
    digestDetail: (time: string) => `Every evening at ${time}`,
    digestSkipped: 'Skipped',
    cta: 'Go to Dashboard',
  },
} as const;

/**
 * The default name of the household the fork creates. Identical to what
 * `users-provision` named the household it used to create at sign-in, so a
 * parent sees the same "Ana's Household" on the profile step either way.
 */
export function householdNameFor(displayName: string): string {
  return `${displayName}'s Household`;
}
